/**
 * Who is working (1.1): the roster grid over a span. Cells read the real shifts
 * and blocks from the horizon; PTO is derived (a standing-pattern day with no
 * shift), so time-off entered in the product shows up here on the next poll.
 *
 * **Every cell is a real shift.** The grid used to grey out anything past four
 * weeks as a "standing pattern, not yet published", which is a state the product
 * does not have: its nightly worker generates shifts out to the largest
 * `daysBeforeLastOption` of any visit type a provider serves. The seed and the
 * horizon read now reach thirteen weeks, so there is nothing left to assume and
 * the fallback is gone (Pete, 2026-09-20).
 */
import type { DateKey, DemoState, RosterEntry } from "@caredna-marlin/utils/homeHealthDemo/state";
import { STANDING_PATTERNS } from "@caredna-marlin/utils/homeHealthDemo/fixtures/standingPatterns";
import { addDaysToKey, dateKeyRange, formatInBranchZone, isoWeekday, selectDemoNow, toDateKey, weekStartKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { clinicianDayFacts, dayBounds, overlapMinutes, providerIdFor } from "./clinicianDay";
import { isInRamp, isSocCapable, rosterEntries, supplyTerritoriesFor } from "./roster";

/** Section order on the grid: nursing, then therapy, then the rest. */
const DISCIPLINE_ORDER = ["RN", "LPN", "PT", "PTA", "OT", "COTA", "SLP", "MSW", "HHA"] as const;

export type RosterSpan = 2 | 4 | 13;

export type RosterCellKind =
  | "off" // no shift, not expected
  | "shift" // standing shift
  | "worked" // a past day with a shift
  | "pto" // pattern day with no shift → approved time off (shift removed via availability)
  | "ramp" // orientation ramp block on a shift day
  | "overflow" // overflow shift
  | "undeclared"; // no shift and no standing pattern to assume one way or the other — availability unknown

/** A day's shift bounds as branch-zone `"HH:mm"` wall clocks. The view phrases them. */
export interface RosterHours {
  start: string;
  end: string;
}

export interface RosterCell {
  dateKey: DateKey;
  kind: RosterCellKind;
  /** The cell's own text where it is not a span of hours: "PTO", "OVF", "✓", "—". */
  label: string;
  /** A published shift or a standing pattern: the hours, phrased by the view. */
  hours?: RosterHours;
  /** Compact glyph for the 4-week grid. */
  glyph: string;
  title: string;
  rampHours?: number;
  socCapable: boolean;
  isToday: boolean;
  weekend: boolean;
}

export interface RosterRow {
  handle: string;
  displayName: string;
  meta: string;
  cells: RosterCell[];
}

/**
 * One section per discipline, headed by the discipline itself — `RN`, `LPN`,
 * `PT` (Pete, 2026-09-20: *"just show RN, LPN, PT, OT, etc. Don't expand out
 * the names or provide meta-level groupings"*). The old headings rolled
 * disciplines together under branch vocabulary ("Skilled nursing — assessing
 * (RN)", "Therapy"), which buried the one word a reader is scanning for.
 */
export interface RosterGroup {
  key: string;
  label: string;
  rows: RosterRow[];
}

export interface RosterFooter {
  dateKey: DateKey;
  socCapableOnShift: number;
  cliniciansOnShift: number;
}

export interface RosterGrid {
  span: RosterSpan;
  rangeStart: DateKey;
  rangeEnd: DateKey;
  days: DateKey[];
  weeks: DateKey[];
  groups: RosterGroup[];
  footer: RosterFooter[];
}

export interface RosterGridOptions {
  span: RosterSpan;
  /** Monday the grid starts on; defaults to the week containing demo day. */
  weekStart?: DateKey;
  disciplines?: string[];
  territories?: string[];
}

/**
 * The shift's branch-zone `"HH:mm"` bounds. The cell used to phrase them itself,
 * in a twelve-hour shape of its own making ("8–4:30"); the view phrases them now,
 * so a grid cell reads on the same clock as everything else.
 */
function shiftHours(startIso: string, endIso: string, tz: string): RosterHours {
  return { start: formatInBranchZone(new Date(startIso), tz, "HH:mm"), end: formatInBranchZone(new Date(endIso), tz, "HH:mm") };
}

/**
 * The row label's subtext. Pete, 2026-09-20: *"on row labels get rid of all the
 * subtext except external"* — the discipline is the section heading the row sits
 * under, the ramp shows as its own cells, and **SOC-capable** is read off the
 * SOC-capable footer rather than repeated on every name. So the only thing left
 * worth saying beside a name is that the person is not on staff.
 *
 * (Note for the talk track: the roster grid no longer says *SOC-capable pending*
 * beside Priya. The fact is still on screen as the **SOC-capable on shift**
 * footer row, and in Act 4's coverage drawer, but not as a label.)
 */
export function rosterMeta(entry: RosterEntry): string {
  return entry.employment === "perDiem" ? "external" : "";
}

/**
 * An empty cell on a weekday reads "—" rather than nothing (Pete, 2026-09-20:
 * *"always show the dashes in empty cells on weekdays"*), so a gap in the week
 * is visible as a gap. A weekend stays blank and is carried by its shading.
 */
function emptyLabel(weekend: boolean): string {
  return weekend ? "" : "—";
}

/**
 * The last day the snapshot actually carries a shift for **anybody**, which is
 * where the grid's knowledge really ends. Not the same as the range it asked
 * for: the read requests thirteen weeks, and a clone seeded before that change
 * answers with four. Reading the requested edge instead put *Off* on every
 * clinician's weekday for the nine weeks in between, because time off here is
 * inferred from absence and absence is all a short clone has.
 */
function lastDayWithData(state: Pick<DemoState, "server" | "config">): DateKey | undefined {
  const tz = state.config.branch.timeZone;
  let out: DateKey | undefined;
  for (const shift of state.server.shifts) {
    const day = toDateKey(new Date(shift.startIso), tz);
    if (!out || day > out) out = day;
  }
  return out;
}

export function rosterCell(state: DemoState, entry: RosterEntry, dateKey: DateKey, today: DateKey, dataThrough?: DateKey): RosterCell {
  const tz = state.config.branch.timeZone;
  const pattern = STANDING_PATTERNS[entry.handle];
  const weekday = isoWeekday(dateKey);
  const expected = !!pattern && pattern.weekdays.includes(weekday) && !pattern.perDiem;
  const weekend = weekday >= 6;
  const isToday = dateKey === today;
  const socCapable = isSocCapable(entry, dateKey);
  const base = { dateKey, socCapable, isToday, weekend };

  /**
   * **Past the data the grid says nothing at all** — no dash, no *Off*, no
   * greyed pattern (Pete, 2026-09-20: *"can you just blank out things past the
   * horizon date instead of showing them as off?"*). Every state this grid draws
   * is read or inferred from shifts, so where there are no shifts there is
   * nothing to draw, and an empty cell is the only honest mark. It is also
   * self-correcting: widen the seed and the blank retreats on its own.
   *
   * The invented state — a greyed "standing pattern, not yet published" — is
   * gone for good; the product generates shifts out to the scheduling limit and
   * so does the seed. This is only the admission that a *short* clone exists.
   */
  if (dataThrough && dateKey > dataThrough) {
    return { ...base, kind: "off", label: "", glyph: "", title: "", socCapable: false };
  }

  const facts = clinicianDayFacts(state, entry, dateKey);
  if (!facts.hasShift) {
    // Time off is inferred from absence, so it may only be inferred inside the
    // data — which the guard above has already established we are.
    if (expected && !state.sandbox?.removedHandles.includes(entry.handle)) return { ...base, kind: "pto", label: "Off", glyph: "Off", title: "approved time off (shift removed via availability)", socCapable: false };
    if (pattern?.perDiem) return { ...base, kind: "undeclared", label: emptyLabel(weekend), glyph: "·", title: "availability unknown", socCapable: false };
    return { ...base, kind: "off", label: emptyLabel(weekend), glyph: "", title: "", socCapable: false };
  }

  const providerId = providerIdFor(state, entry);
  const { start, end } = dayBounds(dateKey, tz);
  const shifts = state.server.shifts.filter((s) => s.providerId === providerId && s.status === "confirmed" && overlapMinutes(s.startIso, s.endIso, start, end) > 0);
  const primary = shifts.find((s) => !s.overflow) ?? shifts[0];
  const hours = primary ? shiftHours(primary.startIso, primary.endIso, tz) : undefined;
  const rampHours = facts.blockMinutes && isInRamp(entry, dateKey) ? Math.round(facts.blockMinutes / 60) : undefined;

  if (rampHours) return { ...base, kind: "ramp", label: `R ${rampHours}h`, glyph: `R${rampHours}`, title: `orientation ramp: ${rampHours}h/day blocked · no solo SOCs`, rampHours, socCapable: false };
  // An overflow day and a day already worked both read as the hours they are —
  // Pete, 2026-09-20: *"instead of showing OVF for overflow shifts just show the
  // time like the other shifts"* and *"show the actual shift hour range for past
  // shifts instead of a check"*. The colour still says which is which; the text
  // no longer has to carry two vocabularies.
  // Past first: a day already worked reads as worked whether or not the shift
  // that covered it was an overflow one. Overflow is a thing you say about a day
  // still to come — it is where the next visit would go — and saying it about
  // Saturday two weeks ago left one blue cell in a row of grey ones.
  if (dateKey < today) return { ...base, kind: "worked", label: "", hours, glyph: "✓", title: "worked (actual)" };
  if (shifts.length && shifts.every((s) => s.overflow)) return { ...base, kind: "overflow", label: "", hours, glyph: "O", title: "overflow shift (used last)" };
  // The title says "standing shift" and the view adds the hours it just phrased.
  return { ...base, kind: "shift", label: "", hours, glyph: "▮", title: "standing shift" };
}

export function selectRosterGrid(state: DemoState, opts: RosterGridOptions): RosterGrid {
  const tz = state.config.branch.timeZone;
  const today = toDateKey(selectDemoNow(state, new Date()), tz);
  const weekStart = opts.weekStart ?? weekStartKey(state.meta.demoToday);
  const rangeStart = weekStart;
  const rangeEnd = addDaysToKey(weekStart, opts.span * 7 - 1);
  const days = dateKeyRange(rangeStart, rangeEnd);
  const weeks = days.filter((d) => isoWeekday(d) === 1);
  const entries = rosterEntries(state)
    .filter((e) => !opts.disciplines || opts.disciplines.includes(e.discipline))
    .filter((e) => !opts.territories || supplyTerritoriesFor(e, state.config.branch).some((t) => opts.territories!.includes(t)));
  const dataThrough = lastDayWithData(state);
  const groups: RosterGroup[] = DISCIPLINE_ORDER.map((discipline) => ({
    key: discipline,
    label: discipline,
    rows: entries
      .filter((e) => e.discipline === discipline)
      .map((e) => ({ handle: e.handle, displayName: e.displayName, meta: rosterMeta(e), cells: days.map((d) => rosterCell(state, e, d, today, dataThrough)) })),
  })).filter((g) => g.rows.length > 0);
  const footer: RosterFooter[] = days.map((d, i) => {
    const cells = groups.flatMap((g) => g.rows.map((r) => r.cells[i]));
    const onShift = cells.filter((c) => c.kind === "shift" || c.kind === "worked" || c.kind === "ramp" || c.kind === "overflow");
    return { dateKey: d, socCapableOnShift: onShift.filter((c) => c.socCapable).length, cliniciansOnShift: onShift.length };
  });
  return { span: opts.span, rangeStart, rangeEnd, days, weeks, groups, footer };
}

/** 13-week view: clinician-days per week per group. Every week is real; see the note at the top. */
export interface RosterWeekRow {
  label: string;
  values: { weekStart: DateKey; count: number }[];
}

export function selectRosterWeeks(state: DemoState, weeks = 13, weekStart?: DateKey): { weeks: DateKey[]; rows: RosterWeekRow[] } {
  const start = weekStart ?? weekStartKey(state.meta.demoToday);
  const weekStarts = Array.from({ length: weeks }, (_, i) => addDaysToKey(start, i * 7));
  const grid = selectRosterGrid(state, { span: 13 as RosterSpan, weekStart: start });
  const rows: RosterWeekRow[] = [];
  const groupsByLabel: Record<string, string[]> = { "RN (assessing)": ["RN"], LPN: ["LPN"], "PT / PTA / OT": ["PT", "PTA", "OT", "COTA"], "MSW / HHA": ["MSW", "HHA"], "SLP (external)": ["SLP"] };
  for (const [label, disciplines] of Object.entries(groupsByLabel)) {
    const rowsIn = grid.groups.flatMap((g) => g.rows).filter((r) => disciplines.includes(state.roster[r.handle]?.discipline ?? ""));
    rows.push({
      label,
      values: weekStarts.map((ws) => {
        const weekDays = dateKeyRange(ws, addDaysToKey(ws, 6));
        const idx = weekDays.map((d) => grid.days.indexOf(d)).filter((i) => i >= 0);
        const count = rowsIn.reduce((s, r) => s + idx.filter((i) => ["shift", "worked", "ramp", "overflow"].includes(r.cells[i].kind)).length, 0);
        return { weekStart: ws, count };
      }),
    });
  }
  const socRow: RosterWeekRow = {
    label: "SOC-capable clinician-days",
    values: weekStarts.map((ws) => {
      const weekDays = dateKeyRange(ws, addDaysToKey(ws, 6));
      const idx = weekDays.map((d) => grid.days.indexOf(d)).filter((i) => i >= 0);
      return { weekStart: ws, count: idx.reduce((s, i) => s + grid.footer[i].socCapableOnShift, 0) };
    }),
  };
  rows.push(socRow);
  return { weeks: weekStarts, rows };
}
